import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, BookOpen, ArrowRight, Sparkles } from "lucide-react";

const PILLARS = [
  { v: "", l: "All pillars" },
  { v: "discover", l: "Discover" },
  { v: "learn", l: "Learn" },
  { v: "practice", l: "Practice" },
  { v: "earn", l: "Earn" },
  { v: "advance", l: "Advance" },
  { v: "protect", l: "Protect" },
];

export default function KnowledgeHub() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [pillar, setPillar] = useState(params.get("pillar") || "");
  const [chapters, setChapters] = useState([]);
  const [chunks, setChunks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      if (q.trim()) {
        const [chRes, chunkRes] = await Promise.all([
          api.get(`/kb/chapters?limit=24${pillar ? `&pillar=${pillar}` : ""}&q=${encodeURIComponent(q)}`),
          api.post("/kb/search", { q, pillar: pillar || undefined, limit: 10 }),
        ]);
        setChapters(chRes.data.items || []);
        setTotal(chRes.data.total || 0);
        setChunks(chunkRes.data.items || []);
      } else {
        const chRes = await api.get(`/kb/chapters?limit=48${pillar ? `&pillar=${pillar}` : ""}`);
        setChapters(chRes.data.items || []);
        setTotal(chRes.data.total || 0);
        setChunks([]);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/kb/stats").then(r => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pillar]);

  const submit = (e) => {
    e?.preventDefault?.();
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (pillar) next.set("pillar", pillar);
    setParams(next);
    load();
  };

  return (
    <div className="space-y-8" data-testid="knowledge-hub">
      <div>
        <div className="label-caps mb-2">Pillar · Learn</div>
        <h1 className="text-3xl sm:text-5xl tracking-tight font-semibold">The library.</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          {stats ? `${stats.chapters?.toLocaleString()} chapters across ${stats.parts} volumes.` : "Loading…"}{" "}
          Semantic text search, pillar filters, cited chapter reader.
        </p>
      </div>

      <form onSubmit={submit}>
        <Card className="p-3 md:p-4 flex flex-col md:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search 2,000+ pages — e.g. decision rights, succession, retaliation remedies…"
              data-testid="kb-search-input"
              className="pl-9 h-11 border-0 bg-transparent focus-visible:ring-0" />
          </div>
          <Select value={pillar} onValueChange={setPillar}>
            <SelectTrigger className="h-11 md:w-48" data-testid="kb-pillar"><SelectValue placeholder="All pillars" /></SelectTrigger>
            <SelectContent>
              {PILLARS.map((p) => <SelectItem key={p.v || "all"} value={p.v || " "}>{p.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button type="submit" className="rounded-full h-11 px-5" data-testid="kb-search-btn">Search</Button>
        </Card>
      </form>

      {chunks.length > 0 && (
        <div data-testid="kb-passages">
          <div className="label-caps mb-3">Top passages</div>
          <div className="space-y-3">
            {chunks.slice(0, 5).map((c, i) => (
              <Link key={i} to={`/app/learn/chapter/${c.chapter_id}`} className="block">
                <Card className="p-5 hover:border-primary/50 transition-colors duration-200">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="text-sm font-medium truncate">{c.title}</div>
                    <Badge variant="outline" className="text-[10px] font-mono shrink-0">{c.topic}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">{c.text}</p>
                  <div className="mt-3 text-[11px] font-mono text-muted-foreground flex items-center gap-1">
                    Open chapter <ArrowRight className="h-3 w-3" />
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="label-caps">
            {q ? `Chapters matching "${q}"` : "Chapters"} · {total.toLocaleString()}
          </div>
        </div>
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({length: 9}).map((_, i) => <Skeleton key={i} className="h-28" />)}
          </div>
        ) : chapters.length === 0 ? (
          <Card className="p-10 text-center text-muted-foreground">
            <BookOpen className="h-6 w-6 mx-auto mb-2" />
            No chapters match. Try different terms or clear the pillar filter.
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {chapters.map((c) => (
              <Link key={c.id} to={`/app/learn/chapter/${c.id}`} data-testid={`kb-ch-${c.id}`}>
                <Card className="p-5 h-full hover:border-primary/50 transition-colors duration-200">
                  <div className="label-caps text-[9px] mb-1.5">{c.section}</div>
                  <div className="text-sm font-medium leading-snug">{c.title}</div>
                  <div className="mt-2 text-xs text-muted-foreground line-clamp-2">{c.topic}</div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {(c.pillars || []).map((p) => (
                      <Badge key={p} variant="secondary" className="text-[9px] font-mono uppercase">{p}</Badge>
                    ))}
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
